import { useSpring } from "@react-spring/web";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import router, { ROUTES } from "../../../app/router";
import { RootState } from "../../../shared/redux/store";
import {
  doPolygonsIntersect,
  rotateBird,
} from "../../../shared/redux/game/checkProcess";
import {
  incrementCount,
  setPause,
} from "../../../shared/redux/game/slice";
import {
  resetObstacles,
  setElementCounted,
} from "../../../shared/redux/obstacles/slice";
import { stateObsType } from "../../../shared/redux/obstacles/types";
import {
  BLUE_LINE_OFFSET_LARGE,
  BLUE_LINE_OFFSET_SMALL,
  BIRD_HEIGHT,
  BIRD_TOP_FAIL_OFFSET,
  BIRD_WIDTH,
  BOTTOM_OBS_X_OFFSET,
  BOTTOM_OBS_Y_OFFSET,
  COLLISION_CHECK_LIMIT_X,
  COLLISION_FAIL_DELAY_MS,
  CONTAINER_HEIGHT_LARGE,
  CONTAINER_HEIGHT_SMALL,
  CONTAINER_SHIFT_INTERVAL_MS,
  CONTAINER_SPEED_STEP,
  ENABLE_REPEAT_CLICK_DELAY_MS,
  FAIL_BALL_BOTTOM_OFFSET,
  FAIL_POPUP_DELAY_MS,
  FALL_INTERVAL_MS,
  FALL_STEP,
  FIRST_CLICK_ANIMATION_DELAY_MS,
  FIRST_CLICK_BOOST_LARGE,
  FIRST_CLICK_BOOST_MEDIUM,
  FIRST_CLICK_BOOST_SMALL,
  FIRST_CLICK_SHIFT_LEFT_DESKTOP,
  FIRST_CLICK_SHIFT_LEFT_MOBILE,
  INITIAL_BALL_BOTTOM,
  INITIAL_BALL_LEFT,
  INITIAL_CONTAINER_LEFT,
  INITIAL_CONTAINER_SPEED,
  JUMP_OFFSET,
  ROTATE_DOWN_DEG,
  ROTATE_DOWN_DELAY_MS,
  ROTATE_UP_DEG,
  SCORE_PASS_LIMIT_X,
  SPRING_CONFIG,
  TOP_BORDER_FAIL_DELAY_MS,
  TOP_OBS_X_OFFSET,
  TOP_OBS_Y_OFFSET,
  WINDOW_HEIGHT_LARGE,
  WINDOW_HEIGHT_MEDIUM,
  WINDOW_WIDTH_MOBILE,
} from "./constants";

type Rect = {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
};

type Vertex = {
  x: number;
  y: number;
};

const getContainerHeight = (windowHeight: number): number => {
  if (windowHeight > WINDOW_HEIGHT_LARGE) {
    return CONTAINER_HEIGHT_LARGE;
  }

  return CONTAINER_HEIGHT_SMALL;
};

const getBlueLineBounds = (windowHeight: number, containerHeight: number) => {
  const halfWindowHeight = windowHeight / 2;
  const offset =
    windowHeight > WINDOW_HEIGHT_LARGE
      ? BLUE_LINE_OFFSET_LARGE
      : BLUE_LINE_OFFSET_SMALL;
  const bottomBlueLine = windowHeight - halfWindowHeight - offset;
  const topBlueLine = halfWindowHeight - offset;

  return { halfWindowHeight, bottomBlueLine, topBlueLine, containerHeight };
};

const getFirstClickBoost = (windowHeight: number): number => {
  if (windowHeight > WINDOW_HEIGHT_LARGE) {
    return FIRST_CLICK_BOOST_LARGE;
  }

  if (windowHeight > WINDOW_HEIGHT_MEDIUM) {
    return FIRST_CLICK_BOOST_MEDIUM;
  }

  return FIRST_CLICK_BOOST_SMALL;
};

const getBirdRect = (birdLeft: number, birdBottom: number): Rect => {
  return {
    x0: birdLeft,
    x1: birdLeft + BIRD_WIDTH,
    y0: window.innerHeight - birdBottom - BIRD_HEIGHT,
    y1: window.innerHeight - birdBottom,
  };
};

const getObstacleRect = (
  obstacle: stateObsType,
  obstacleLeft: number,
  boundary: Vertex[],
  topBlueLine: number,
  containerHeight: number,
): Rect => {
  const x0 = obstacleLeft + boundary[0].x;
  const x1 = obstacle.item.isTop
    ? obstacleLeft + TOP_OBS_X_OFFSET + boundary[1].x
    : obstacleLeft + BOTTOM_OBS_X_OFFSET + boundary[1].x;
  const y0 = obstacle.item.isTop
    ? topBlueLine + boundary[0].y
    : topBlueLine +
      containerHeight -
      obstacle.item.height +
      boundary[0].y +
      BOTTOM_OBS_Y_OFFSET;
  const y1 = obstacle.item.isTop
    ? topBlueLine + boundary[1].y + TOP_OBS_Y_OFFSET
    : topBlueLine + containerHeight - obstacle.item.height + boundary[1].y;

  return { x0, x1, y0, y1 };
};

const isSeparated = (
  birdRect: Rect,
  obstacleRect: Rect,
  isTopObstacle: boolean,
): boolean => {
  if (
    isTopObstacle &&
    birdRect.y0 > obstacleRect.y1 &&
    birdRect.y1 > obstacleRect.y1
  ) {
    return true;
  }

  if (
    !isTopObstacle &&
    birdRect.y0 < obstacleRect.y0 &&
    birdRect.y1 < obstacleRect.y0
  ) {
    return true;
  }

  if (birdRect.x0 < obstacleRect.x0 && birdRect.x1 < obstacleRect.x0) {
    return true;
  }

  if (birdRect.x0 > obstacleRect.x1 && birdRect.x1 > obstacleRect.x1) {
    return true;
  }

  return false;
};

const rectToVertices = (rect: Rect): Vertex[] => {
  return [
    { x: rect.x0, y: rect.y0 },
    { x: rect.x1, y: rect.y0 },
    { x: rect.x1, y: rect.y1 },
    { x: rect.x0, y: rect.y1 },
  ];
};

export const useGameEngine = () => {
  const dispatch = useDispatch();
  const { pause } = useSelector((state: RootState) => state.game);
  const { obstacles } = useSelector((state: RootState) => state.obstacles);

  const [firstClick, setFirstClick] = useState(true);
  const [isFail, setIsFail] = useState(false);
  const [canClick, setCanClick] = useState(false);
  const [isTotalFail, setTotalFail] = useState(false);

  const touchStartedRef = useRef(false);
  const rotateValueRef = useRef(0);
  const firstClickRef = useRef(firstClick);

  const containerLeftRef = useRef(INITIAL_CONTAINER_LEFT);
  const containerSpeedRef = useRef(INITIAL_CONTAINER_SPEED);
  const bottomBallRef = useRef(INITIAL_BALL_BOTTOM);
  const leftBallRef = useRef(INITIAL_BALL_LEFT);
  const obstaclesRef = useRef<stateObsType[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalBallRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const intervalContainerRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const [springPropsContainer, setSpringPropsContainer] = useSpring(() => ({
    transform: "translate3D(0px, 0px, 0px)",
    config: SPRING_CONFIG,
  }));

  const [springPropsBall, setSpringPropsBall] = useSpring(() => ({
    bottom: `${INITIAL_BALL_BOTTOM}px`,
    left: `${INITIAL_BALL_LEFT}px`,
    transform: "rotate(0deg)",
    config: SPRING_CONFIG,
  }));

  const windowHeight = window.innerHeight;
  const containerHeight = getContainerHeight(windowHeight);
  const { halfWindowHeight, bottomBlueLine, topBlueLine } = getBlueLineBounds(
    windowHeight,
    containerHeight,
  );

  const focusContainer = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
  }, []);

  const clearDropTimer = useCallback(() => {
    if (!dropTimerRef.current) return;

    clearTimeout(dropTimerRef.current);
    dropTimerRef.current = null;
  }, []);

  const stopBallInterval = useCallback(() => {
    if (!intervalBallRef.current) return;

    clearInterval(intervalBallRef.current);
    intervalBallRef.current = null;
  }, []);

  const stopContainerInterval = useCallback(() => {
    if (!intervalContainerRef.current) return;

    clearInterval(intervalContainerRef.current);
    intervalContainerRef.current = null;
  }, []);

  const stopMovement = useCallback(() => {
    stopBallInterval();
    stopContainerInterval();
  }, [stopBallInterval, stopContainerInterval]);

  const checkProcess = useCallback(() => {
    obstaclesRef.current.forEach((obstacle, obstacleIndex) => {
      if (obstacle.counted) return;

      const obstacleLeft = containerLeftRef.current + obstacle.absoluteLeft;
      if (obstacleLeft >= COLLISION_CHECK_LIMIT_X) return;

      const birdRect = getBirdRect(leftBallRef.current, bottomBallRef.current);
      const passMoment = obstacleLeft + obstacle.item.width;
      if (passMoment < SCORE_PASS_LIMIT_X) {
        dispatch(incrementCount());
        dispatch(setElementCounted(obstacleIndex));
        return;
      }

      obstacle.boundaries.forEach((boundary) => {
        const obstacleRect = getObstacleRect(
          obstacle,
          obstacleLeft,
          boundary,
          topBlueLine,
          containerHeight,
        );
        const skipCollisionCheck = isSeparated(
          birdRect,
          obstacleRect,
          obstacle.item.isTop,
        );

        if (skipCollisionCheck) {
          return;
        }

        const birdVertices = rotateBird(
          rectToVertices(birdRect),
          rotateValueRef.current,
        );
        const obstacleVertices = rectToVertices(obstacleRect);
        const isCollision = doPolygonsIntersect(birdVertices, obstacleVertices);

        if (isCollision) {
          setTimeout(() => {
            setIsFail(true);
          }, COLLISION_FAIL_DELAY_MS);
        }
      });
    });
  }, [containerHeight, dispatch, topBlueLine]);

  const startIntervalContainer = useCallback(() => {
    stopContainerInterval();

    intervalContainerRef.current = setInterval(() => {
      containerLeftRef.current -= containerSpeedRef.current;
      setSpringPropsContainer({
        transform: `translate3D(${containerLeftRef.current}px, 0px, 0px)`,
      });
      checkProcess();
      containerSpeedRef.current += CONTAINER_SPEED_STEP;
    }, CONTAINER_SHIFT_INTERVAL_MS);
  }, [checkProcess, setSpringPropsContainer, stopContainerInterval]);

  const startIntervalBall = useCallback(
    (isClick: boolean) => {
      if (pause || isClick) {
        stopBallInterval();
        return;
      }

      stopBallInterval();

      intervalBallRef.current = setInterval(() => {
        bottomBallRef.current -= FALL_STEP;

        setSpringPropsBall({
          bottom: `${bottomBallRef.current}px`,
        });

        if (bottomBallRef.current < bottomBlueLine) {
          setIsFail(true);
        }
      }, FALL_INTERVAL_MS);
    },
    [bottomBlueLine, pause, setSpringPropsBall, stopBallInterval],
  );

  const onAction = useCallback(() => {
    if (pause) {
      stopMovement();
      return;
    }

    clearDropTimer();

    if (!firstClick) {
      if (canClick) {
        bottomBallRef.current += JUMP_OFFSET;
      }
      setSpringPropsBall({
        bottom: `${bottomBallRef.current}px`,
        left: `${leftBallRef.current}px`,
        transform: `rotate(${ROTATE_UP_DEG}deg)`,
      });
      rotateValueRef.current = ROTATE_UP_DEG;
      startIntervalBall(true);
    } else {
      setTimeout(() => {
        setCanClick(true);
      }, ENABLE_REPEAT_CLICK_DELAY_MS);

      startIntervalContainer();
      bottomBallRef.current += getFirstClickBoost(windowHeight);
      leftBallRef.current +=
        window.innerWidth < WINDOW_WIDTH_MOBILE
          ? FIRST_CLICK_SHIFT_LEFT_MOBILE
          : FIRST_CLICK_SHIFT_LEFT_DESKTOP;

      setTimeout(() => {
        setSpringPropsBall({
          bottom: `${bottomBallRef.current}px`,
          left: `${leftBallRef.current}px`,
          transform: `rotate(${ROTATE_UP_DEG}deg)`,
        });
        rotateValueRef.current = ROTATE_UP_DEG;
      }, FIRST_CLICK_ANIMATION_DELAY_MS);
      setFirstClick(false);
    }

    const isTooHigh =
      bottomBallRef.current + BIRD_TOP_FAIL_OFFSET >
      halfWindowHeight + containerHeight / 2;
    if (isTooHigh) {
      setTimeout(() => {
        setIsFail(true);
      }, TOP_BORDER_FAIL_DELAY_MS);
    }

    dropTimerRef.current = setTimeout(() => {
      startIntervalBall(false);
      setSpringPropsBall({
        transform: `rotate(${ROTATE_DOWN_DEG}deg)`,
      });
      rotateValueRef.current = ROTATE_DOWN_DEG;
    }, ROTATE_DOWN_DELAY_MS);
  }, [
    canClick,
    clearDropTimer,
    containerHeight,
    firstClick,
    halfWindowHeight,
    pause,
    setSpringPropsBall,
    startIntervalBall,
    startIntervalContainer,
    stopMovement,
    windowHeight,
  ]);

  const onContainerTouchStart = useCallback(() => {
    onAction();
    touchStartedRef.current = true;
  }, [onAction]);

  const onContainerClick = useCallback(() => {
    if (touchStartedRef.current) {
      touchStartedRef.current = false;
      return;
    }

    onAction();
  }, [onAction]);

  const onContainerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.code === "Space") {
        onAction();
      }
    },
    [onAction],
  );

  useEffect(() => {
    firstClickRef.current = firstClick;
  }, [firstClick]);

  useEffect(() => {
    containerLeftRef.current = INITIAL_CONTAINER_LEFT;
    containerSpeedRef.current = INITIAL_CONTAINER_SPEED;
    obstaclesRef.current = [];

    return () => {
      stopMovement();
      clearDropTimer();
    };
  }, [clearDropTimer, dispatch, stopMovement]);

  useEffect(() => {
    if (isFail && !isTotalFail) {
      bottomBallRef.current = window.innerHeight / 2 - FAIL_BALL_BOTTOM_OFFSET;
      setSpringPropsBall({
        bottom: `${bottomBallRef.current}px`,
      });

      dispatch(setPause(true));
      setTimeout(() => {
        router.navigate(ROUTES.FINAL);
        dispatch(resetObstacles());
      }, FAIL_POPUP_DELAY_MS);
      setTotalFail(true);
      setIsFail(false);
    }
  }, [dispatch, isFail, isTotalFail, setSpringPropsBall]);

  useEffect(() => {
    const isFirstClick = firstClickRef.current;

    if (pause) {
      stopMovement();
    } else if (!isFirstClick) {
      startIntervalBall(false);
      startIntervalContainer();
      focusContainer();
    }
  }, [
    dispatch,
    focusContainer,
    pause,
    startIntervalBall,
    startIntervalContainer,
    stopMovement,
  ]);

  useEffect(() => {
    obstaclesRef.current = obstacles;
  }, [obstacles]);

  useEffect(() => {
    focusContainer();
  }, [focusContainer]);

  return {
    firstClick,
    containerRef,
    springPropsBall,
    springPropsContainer,
    onContainerTouchStart,
    onContainerClick,
    onContainerKeyDown,
  };
};
